import { useState, useEffect, useRef, useCallback } from "react";
import { X, Download, Share } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "pwa-install-dismissed";
const DISMISS_DAYS = 14;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as any).standalone === true;

const isDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const { timestamp } = JSON.parse(raw);
    return Date.now() - timestamp < DISMISS_DAYS * 86400000;
  } catch {
    return false;
  }
};

const InstallBanner = () => {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState<"android" | "ios" | null>(null);
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ timestamp: Date.now() }));
    setShow(false);
  }, []);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setPlatform("android");
      setShow(true);
    };

    const onInstalled = () => setShow(false);

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // iOS fallback
    if (isIOS()) {
      setPlatform("ios");
      setShow(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt.current) return;
    await deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === "accepted") setShow(false);
    deferredPrompt.current = null;
  };

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-label="Install app"
      className="fixed bottom-4 inset-x-4 z-[90] rounded-xl bg-[hsl(25,30%,15%)] text-white shadow-2xl p-4 md:max-w-md md:mx-auto"
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss install banner"
        className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/10 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>

      <a href="/install" className="flex items-center gap-3 pr-6 no-underline text-inherit">
        <Download className="h-8 w-8 text-gold shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-heading text-sm uppercase tracking-wide">
            Add to Home Screen
          </p>
          <p className="text-xs text-white/70 mt-1">
            Tap here to learn how — it's easy!
          </p>
        </div>
        <span className="shrink-0 inline-flex items-center justify-center rounded-md bg-gold text-wood px-3 py-1.5 font-heading uppercase text-xs tracking-wide">
          How To
        </span>
      </a>
    </div>
  );
};

export default InstallBanner;
